/**
 * 配送时间预览组件配置
 */
const CONFIG = {
  GEO_LOCATION_URL: 'https://ip.cocochairs.com/',
  US_DISPLAY: '🇺🇸 US',
  LOCATION_CACHE_KEY: 'user-location-cache', // 全局位置缓存（不依赖产品）
  LOCATION_CACHE_HOURS: 24 // 位置缓存时间更长
};

// DOM 元素缓存
const elements = {};

/**
 * 获取 DOM 元素（带缓存）
 */
function getElement(id) {
  if (!elements[id]) {
    elements[id] = document.getElementById(id);
  }
  return elements[id];
}

/**
 * 本地缓存（localStorage，带 TTL，不随请求发送）
 */
function setCache(key, value, hours) {
  try {
    localStorage.setItem(key, JSON.stringify({ v: value, exp: Date.now() + hours * 3600 * 1000 }));
  } catch (e) {}
}

function getCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const item = JSON.parse(raw);
    if (Date.now() > item.exp) {
      localStorage.removeItem(key);
      return null;
    }
    return item.v;
  } catch (e) {
    return null;
  }
}

/**
 * 根据邮编获取配送类别（仅用于美国）
 */
function getZipcodeCategory(zipcode) {
  if (!zipcode) return '4';
  
  // 移除邮编中的 "-" 和空格
  const zipcodeClean = zipcode.replace(/-/g, '').replace(/\s/g, '');
  
  if (zipcodeClean.length < 2) return '4';
  
  const firstDigit = zipcodeClean[0] && /^\d/.test(zipcodeClean) ? parseInt(zipcodeClean[0]) : 0;
  const twoDigit = zipcodeClean.length >= 2 && /^\d{2}/.test(zipcodeClean) ? parseInt(zipcodeClean.substring(0, 2)) : 0;
  
  if (90 <= twoDigit && twoDigit <= 92) {
    return '1';
  } else if (firstDigit === 8 || firstDigit === 9) {
    return '2';
  } else if (5 <= firstDigit && firstDigit <= 7) {
    return '3';
  } else {
    return '4';
  }
}

/**
 * 根据产品标签选择产品类型
 */
function getProductType(productTags) {
  const tags = (productTags || '').toLowerCase();
  if (tags.includes('small-item') || tags.includes('air transport')) {
    return 'small';
  } else if (tags.includes('express-item') || tags.includes('2 weeks')) {
    return 'express';
  }
  return 'big';
}

/**
 * 根据产品ID和标签选择配送规则
 */
function selectDeliveryRule(deliveryRules, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown) {
  if (!deliveryRules) {
    return null;
  }

  const productIdString = String(productId);
  const productType = getProductType(productTags);

  // 优先检查 custom 中是否有该产品ID的配置
  if (deliveryRules.custom && deliveryRules.custom[productIdString]) {
    const customRule = deliveryRules.custom[productIdString];
    return {
      prefix_title: deliveryTimePrefix || '',
      prefix_title_unknown: deliveryTimePrefixUnknown || '',
      delivery_rules: customRule,
      fallback_rules: deliveryRules[productType], // 保存回退规则
      is_custom: true
    };
  }

  // 根据产品标签选择类型
  const rule = deliveryRules[productType];
  if (!rule) {
    return null;
  }

  return {
    prefix_title: deliveryTimePrefix || '',
    prefix_title_unknown: deliveryTimePrefixUnknown || '',
    delivery_rules: rule,
    is_custom: false
  };
}

/**
 * 计算配送时间
 */
function calculateDeliveryTime(deliveryConfig, countryCode, zipcode) {
  if (!deliveryConfig || !deliveryConfig.delivery_rules) {
    return {
      success: false,
      prefix_title_unknown: deliveryConfig?.prefix_title_unknown || '',
      delivery_time_unknown: ''
    };
  }

  const deliveryRules = deliveryConfig.delivery_rules;
  const countryRules = deliveryRules[countryCode];
  
  // 如果在 custom 规则中找不到对应国家，且存在回退规则，则使用回退规则
  if (!countryRules && deliveryConfig.is_custom && deliveryConfig.fallback_rules) {
    const fallbackCountryRules = deliveryConfig.fallback_rules[countryCode];
    if (fallbackCountryRules) {
      // 使用回退规则计算
      return calculateDeliveryTimeFromRules(
        fallbackCountryRules,
        countryCode,
        zipcode,
        deliveryConfig.prefix_title
      );
    }
    // 回退规则中也没有，尝试使用 XXX
    const fallbackDefault = deliveryConfig.fallback_rules['XXX'];
    if (fallbackDefault) {
      const defaultTime = typeof fallbackDefault === 'string' ? fallbackDefault : (fallbackDefault['4'] || fallbackDefault.default);
      if (defaultTime) {
        return {
          success: true,
          delivery_time: defaultTime,
          prefix_title: deliveryConfig.prefix_title || ''
        };
      }
    }
  }
  
  if (!countryRules) {
    // 尝试使用 XXX 作为默认值
    const defaultRules = deliveryRules['XXX'];
    if (defaultRules) {
      const defaultTime = typeof defaultRules === 'string' ? defaultRules : (defaultRules['4'] || defaultRules.default);
      if (defaultTime) {
        return {
          success: true,
          delivery_time: defaultTime,
          prefix_title: deliveryConfig.prefix_title || ''
        };
      }
    }
    return {
      success: false,
      prefix_title_unknown: deliveryConfig.prefix_title_unknown || '',
      delivery_time_unknown: ''
    };
  }

  // 使用当前规则计算
  return calculateDeliveryTimeFromRules(
    countryRules,
    countryCode,
    zipcode,
    deliveryConfig.prefix_title
  );
}

/**
 * 从规则中计算配送时间（辅助函数）
 */
function calculateDeliveryTimeFromRules(countryRules, countryCode, zipcode, deliveryTimePrefix) {
  // 判断 countryRules 是对象（美国邮编分类）还是字符串（其他国家固定时间）
  if (typeof countryRules === 'string') {
    // 非美国：直接使用固定配送时间
    return {
      success: true,
      delivery_time: countryRules,
      prefix_title: deliveryTimePrefix || ''
    };
  } else if (typeof countryRules === 'object') {
    // 美国：根据邮编分类查找配送时间
    if (countryCode === 'US' && zipcode) {
      const category = getZipcodeCategory(zipcode);
      const deliveryTime = countryRules[category];
      
      if (deliveryTime) {
        return {
          success: true,
          delivery_time: deliveryTime,
          prefix_title: deliveryTimePrefix || ''
        };
      }
    }
    // 如果没有邮编或找不到对应分类，使用类别4作为默认值
    const defaultTime = countryRules['4'] || countryRules.default;
    if (defaultTime) {
      return {
        success: true,
        delivery_time: defaultTime,
        prefix_title: deliveryTimePrefix || ''
      };
    }
  }

  return {
    success: false,
    prefix_title_unknown: '',
    delivery_time_unknown: ''
  };
}

/**
 * 获取用户位置信息（通过自建 Cloudflare Worker 调用 ip-api.com，结果缓存到 localStorage）
 */
async function getUserLocationByIP() {
  // 先检查全局位置缓存（不依赖产品ID）
  const locationCache = getCache(CONFIG.LOCATION_CACHE_KEY);
  if (locationCache) {
    return locationCache;
  }

  try {
    const response = await fetch(CONFIG.GEO_LOCATION_URL);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    if (data.status !== 'success') {
      return null;
    }

    const location = {
      country_code: data.countryCode || '',
      zipcode: data.zip || '',
      city: data.city || ''
    };

    // 缓存位置信息（全局缓存，不依赖产品）
    setCache(CONFIG.LOCATION_CACHE_KEY, location, CONFIG.LOCATION_CACHE_HOURS);
    return location;
  } catch (error) {
    return null;
  }
}

/**
 * 获取配送信息
 */
async function fetchDeliveryInfo(productId, deliveryRules, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown) {
  try {
    // 根据产品ID和标签选择配送规则
    const deliveryConfig = selectDeliveryRule(deliveryRules, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
    if (!deliveryConfig) {
      return {
        country_code: '',
        city: '',
        zipcode: '',
        delivery_time: '',
        prefix_title: '',
        prefix_title_unknown: '',
        delivery_time_unknown: '',
        ret_code: -1
      };
    }

    // 获取用户位置（通过自建 Worker 查询，结果已缓存到 localStorage）
    const location = await getUserLocationByIP();
    if (!location) {
      return {
        country_code: '',
        city: '',
        zipcode: '',
        delivery_time: '',
        prefix_title: '',
        prefix_title_unknown: deliveryConfig.prefix_title_unknown || '',
        delivery_time_unknown: '',
        ret_code: -1
      };
    }

    // 计算配送时间
    const deliveryInfo = calculateDeliveryTime(
      deliveryConfig,
      location.country_code,
      location.zipcode
    );

    // 构建返回数据
    const result = {
      country_code: location.country_code,
      city: location.city,
      zipcode: location.zipcode,
      delivery_time: deliveryInfo.delivery_time || '',
      prefix_title: deliveryInfo.prefix_title || '',
      prefix_title_unknown: deliveryInfo.prefix_title_unknown || deliveryConfig.prefix_title_unknown || '',
      delivery_time_unknown: deliveryInfo.delivery_time_unknown || '',
      ret_code: deliveryInfo.success ? 0 : -1
    };

    return result;
  } catch (error) {
    console.error('fetch delivery info failed:', error);
    return {
      country_code: '',
      city: '',
      zipcode: '',
      delivery_time: '',
      prefix_title: '',
      prefix_title_unknown: '',
      delivery_time_unknown: '',
      ret_code: -1
    };
  }
}

/**
 * 更新视图
 */
function updateView(locationData, deliveryConfig, countryOptions) {
  if (!locationData) return;

  const { 
    country_code, 
    city, 
    zipcode, 
    delivery_time, 
    prefix_title, 
    prefix_title_unknown, 
    delivery_time_unknown, 
    ret_code 
  } = locationData;

  const addressEl = getElement('address_text_id');
  const timeEl = getElement('delivery_time_id');
  const prefixEl = getElement('delivery_time_prefix_id');
  const countrySelect = getElement('country-select');

  if (!addressEl || !timeEl || !prefixEl) return;

  // 设置国家选择器并获取国家名称
  let country = '';
  if (countrySelect && countryOptions?.option_country && country_code) {
    const countryList = countryOptions.option_country;
    for (let index = 0; index < countryList.length; index++) {
      const name = countryList[index];
      if (typeof name === 'string') {
        // 尝试多种匹配方式
        const parts = name.split(' - ');
        const codeInName = parts.length > 1 ? parts[parts.length - 1].trim() : '';
        if (codeInName === country_code || 
            name.endsWith(country_code) || 
            name.includes(` ${country_code}`) ||
            (country_code === 'US' && (name.includes('US') || name.includes('🇺🇸')))) {
          countrySelect.selectedIndex = index;
          country = name;
          break;
        }
      }
    }
  }

  // 根据 ret_code 决定显示内容
  if (ret_code === 0) {
    // 正常情况：显示 delivery_time 和 prefix_title
    prefixEl.textContent = prefix_title || '';

    // 按照原始逻辑显示地址
    if (zipcode && city) {
      addressEl.textContent = `${zipcode.split('-')[0]} - ${city}`;
    } else if (zipcode) {
      addressEl.textContent = `${country}-${zipcode.split('-')[0]}`;
    } else if (city) {
      addressEl.textContent = city;
    } else {
      addressEl.textContent = country;
    }

    timeEl.textContent = ` : ${delivery_time || ''}`;
  } else {
    // 异常情况或未定义：显示 delivery_time_unknown 和 prefix_title_unknown
    prefixEl.textContent = prefix_title_unknown || '';
    addressEl.textContent = ' ';
    timeEl.textContent = delivery_time_unknown || '';
  }
}


/**
 * 从国家选择器中获取国家代码
 */
function getCountryCodeFromSelect(countrySelect, countryOptions) {
  if (!countrySelect || !countryOptions?.option_country) return '';
  
  const selectedCountry = countrySelect.value;
  for (const country of countryOptions.option_country) {
    if (country === selectedCountry) {
      if (typeof country === 'string') {
        const parts = country.split(' - ');
        if (parts.length > 1) {
          return parts[parts.length - 1].trim();
        } else if (country.includes('US') || country.includes('🇺🇸')) {
          return 'US';
        } else {
          const match = country.match(/\b([A-Z]{2,3})\b/);
          if (match) {
            return match[1];
          }
        }
      }
      break;
    }
  }
  return '';
}

/**
 * 计算配送时间并更新视图
 */
function calculateAndUpdateDeliveryTime(deliveryConfig, countryCode, zipcode, countryOptions) {
  if (!deliveryConfig) return;
  
  const deliveryInfo = calculateDeliveryTime(deliveryConfig, countryCode, zipcode);
  const result = {
    country_code: countryCode,
    city: '',
    zipcode: zipcode,
    delivery_time: deliveryInfo.delivery_time || '',
    prefix_title: deliveryInfo.prefix_title || '',
    prefix_title_unknown: deliveryInfo.prefix_title_unknown || deliveryConfig.prefix_title_unknown || '',
    delivery_time_unknown: deliveryInfo.delivery_time_unknown || '',
    ret_code: deliveryInfo.success ? 0 : -1
  };
  
  updateView(result, deliveryConfig, countryOptions);
}

/**
 * 更新配送信息（手动更新，使用选择的国家和邮编）
 */
async function handleUpdate(deliveryRules, countryOptions, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown) {
  const countrySelect = getElement('country-select');
  const zipcodeInput = getElement('zipcode-input');
  
  if (!countrySelect) return;
  
  // 根据产品ID和标签选择配送规则
  const deliveryConfig = selectDeliveryRule(deliveryRules, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
  if (!deliveryConfig) {
    return;
  }
  
  // 从选择器获取国家代码
  const countryCode = getCountryCodeFromSelect(countrySelect, countryOptions);
  
  // 获取邮编
  const zipcode = zipcodeInput ? zipcodeInput.value.trim() : '';
  
  // 如果选择了美国但没有邮编，使用IP定位
  if (countryCode === 'US' && !zipcode) {
    const data = await fetchDeliveryInfo(productId, deliveryRules, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
    if (data) updateView(data, deliveryConfig, countryOptions);
    return;
  }
  
  // 使用选择的国家和邮编计算配送时间并更新视图
  calculateAndUpdateDeliveryTime(deliveryConfig, countryCode, zipcode, countryOptions);
}

/**
 * 国家选择变化
 */
function handleCountryChange(deliveryRules, countryOptions, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown) {
  const countrySelect = getElement('country-select');
  const zipcodeInput = getElement('zipcode-input');
  const updateButton = getElement('update-button');
  if (!countrySelect || !zipcodeInput || !updateButton) return;

  zipcodeInput.value = '';
  const isUS = countrySelect.value === CONFIG.US_DISPLAY;
  zipcodeInput.disabled = isUS ? '' : 'disabled';
  zipcodeInput.style.display = isUS ? 'block' : 'none';
  updateButton.disabled = isUS ? 'disabled' : '';
  updateButton.style.display = isUS ? 'block' : 'none';

  // 立即显示默认时效
  if (deliveryRules && countryOptions && productId) {
    const countryCode = getCountryCodeFromSelect(countrySelect, countryOptions);
    if (countryCode) {
      const deliveryConfig = selectDeliveryRule(deliveryRules, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
      // 对于非美国国家，直接显示默认时效（不需要邮编）
      // 对于美国，如果没有邮编，显示默认的类别4时效
      calculateAndUpdateDeliveryTime(deliveryConfig, countryCode, '', countryOptions);
    }
  }
}

/**
 * 邮编输入变化
 */
function handleZipcodeChange(deliveryRules, countryOptions, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown) {
  const countrySelect = getElement('country-select');
  const zipcodeInput = getElement('zipcode-input');
  const updateButton = getElement('update-button');
  if (!countrySelect || !zipcodeInput || !updateButton) return;

  if (countrySelect.value === CONFIG.US_DISPLAY) {
    const zipcode = zipcodeInput.value.trim();
    updateButton.disabled = zipcode ? '' : 'disabled';
    
    // 自动更新时效（有邮编时用邮编计算，无邮编时显示默认时效）
    if (deliveryRules && countryOptions && productId) {
      const countryCode = getCountryCodeFromSelect(countrySelect, countryOptions) || 'US';
      const deliveryConfig = selectDeliveryRule(deliveryRules, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
      calculateAndUpdateDeliveryTime(deliveryConfig, countryCode, zipcode, countryOptions);
    }
  }
}

// 存储配置和数据
let deliveryData = null;
let deliveryConfig = null;

/**
 * 提前获取配送数据（不等待 DOM）
 */
function preloadDeliveryData() {
  // 尝试读取配置（如果 DOM 已存在）
  const configEl = document.getElementById('delivery-time-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('parse config failed:', e);
    return;
  }

  deliveryConfig = config;
  const { productId, deliveryRules, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown } = config;

  // 立即开始获取数据
  if (productId && deliveryRules) {
    fetchDeliveryInfo(productId, deliveryRules, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown).then(data => {
      deliveryData = data;
      // 如果 DOM 已准备好，立即更新视图
      if (document.readyState !== 'loading') {
        updateViewWithData();
      }
    });
  }
}

/**
 * 使用已获取的数据更新视图
 */
function updateViewWithData() {
  if (!deliveryData || !deliveryConfig) return;
  const { deliveryRules, productTags, productId, countryConfig, deliveryTimePrefix, deliveryTimePrefixUnknown } = deliveryConfig;
  const deliveryConfigData = selectDeliveryRule(deliveryRules, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
  if (deliveryConfigData) {
    updateView(deliveryData, deliveryConfigData, countryConfig);
  }
}

/**
 * 初始化 DOM 事件
 */
function initDOM() {
  if (!deliveryConfig) {
    // 如果配置还没读取到，尝试读取
    const configEl = getElement('delivery-time-config');
    if (!configEl) return;
    
    try {
      deliveryConfig = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('parse config failed:', e);
      return;
    }
  }

  const { deliveryRules, productTags, countryConfig, productId, deliveryTimePrefix, deliveryTimePrefixUnknown } = deliveryConfig;

  // 确保 expedited 容器始终显示
  const expeditedEl = getElement('delivery-time-expedited-container');
  if (expeditedEl) {
    expeditedEl.style.display = 'block';
  }

  // 如果数据已获取，更新视图
  if (deliveryData) {
    updateViewWithData();
  }

  // 绑定事件
  const updateButton = getElement('update-button');
  const countrySelect = getElement('country-select');
  const zipcodeInput = getElement('zipcode-input');
  const contactLink = getElement('contact_us_id');

  // 初始化按钮显示状态
  if (countrySelect && updateButton) {
    handleCountryChange(deliveryRules, countryConfig, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
  }

  if (updateButton) {
    updateButton.onclick = () => handleUpdate(deliveryRules, countryConfig, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown).catch(console.error);
  }

  if (countrySelect) {
    countrySelect.onchange = () => {
      handleCountryChange(deliveryRules, countryConfig, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
    };
  }

  if (zipcodeInput) {
    zipcodeInput.oninput = () => handleZipcodeChange(deliveryRules, countryConfig, productId, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown);
  }

  if (contactLink) {
    contactLink.onclick = (e) => {
      e.preventDefault();
      try {
        document.querySelector('#ShopifyChat')?.shadowRoot?.querySelector('.chat-toggle')?.click();
      } catch (err) {
        console.error('open chat failed:', err);
      }
    };
  }
}

// 立即尝试预加载数据
preloadDeliveryData();

// 确保 expedited 容器在页面加载时显示
function ensureExpeditedVisible() {
  const expeditedEl = document.getElementById('delivery-time-expedited-container');
  if (expeditedEl) {
    expeditedEl.style.display = 'block';
  }
}

// DOM 准备好后初始化事件
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ensureExpeditedVisible();
    initDOM();
    // 如果数据在 DOM 加载后才获取到，也更新视图
    if (!deliveryData && deliveryConfig) {
      const { productId, deliveryRules, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown } = deliveryConfig;
      if (productId && deliveryRules) {
        fetchDeliveryInfo(productId, deliveryRules, productTags, deliveryTimePrefix, deliveryTimePrefixUnknown).then(data => {
          deliveryData = data;
          updateViewWithData();
        });
      }
    }
  });
} else {
  ensureExpeditedVisible();
  initDOM();
}
